import React, { useRef, useState, useEffect } from 'react';

interface Segment {
  value: string;
  label: string;
}

interface SegmentedControlProps {
  segments: Segment[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function SegmentedControl({ segments, value, onChange, className = '' }: SegmentedControlProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!containerRef.current) return;
    const index = segments.findIndex(s => s.value === value);
    if (index === -1) return;

    const buttons = containerRef.current.querySelectorAll<HTMLButtonElement>('[data-segment]');
    const button = buttons[index];
    if (!button) return;

    setIndicatorStyle({
      width: button.offsetWidth,
      transform: `translateX(${button.offsetLeft}px)`,
    });
  }, [value, segments]);

  return (
    <div
      ref={containerRef}
      className={`relative inline-flex items-center bg-[var(--bg-surface-alt)] rounded-ios-control p-0.5 ${className}`}
    >
      {/* Sliding indicator */}
      <div
        className="absolute top-0.5 left-0 h-[calc(100%-4px)] bg-[var(--bg-surface)] rounded-ios-control-inner shadow-ios-control transition-all duration-200 ease-out"
        style={indicatorStyle}
      />

      {segments.map(segment => (
        <button
          key={segment.value}
          data-segment
          onClick={() => onChange(segment.value)}
          className={`relative z-10 inline-flex items-center justify-center min-h-[44px] px-3 text-[13px] font-medium rounded-ios-control-inner transition-colors duration-200 ${
            value === segment.value
              ? 'text-[var(--text-primary)]'
              : 'text-[var(--text-secondary)]'
          }`}
        >
          {segment.label}
        </button>
      ))}
    </div>
  );
}
